import type { ReactNode } from 'react'
import type { RelatedProblemResult } from '../_state/contracts'
import { RELATED_KIND_LABEL } from './section-heading'

/**
 * A related problem's chip label (LG-360 §8).
 *
 * Duplicates and variants name the far problem directly — a coach comparing
 * "Two Sum" against its chip needs to see which other title it is a
 * duplicate/variant *of*. A series sibling instead names the series and this
 * member's place in it: the far problem's own title says nothing about why it
 * showed up here, where "2 of Best Time to Buy and Sell Stock" does.
 *
 * The kind word itself comes from `RELATED_KIND_LABEL` (expert review B5),
 * not a hand-written ternary — a two-armed `kind === 'duplicate' ? … : …`
 * silently mislabels any *other* kind "Variant", with no compile error, the
 * moment `problemLinkKindEnum` grows a member. `RelatedProblemModal`'s own
 * heading indexes the same `Record`, so the two can no longer disagree.
 */
function chipLabel(item: RelatedProblemResult): string {
  if (item.kind === 'series') {
    const position = item.seriesOrder ?? '?'
    const series = item.seriesName ?? 'a series'
    return `${RELATED_KIND_LABEL.series}: ${position} of ${series}`
  }
  return `${RELATED_KIND_LABEL[item.kind]}: ${item.title}`
}

/**
 * The chip row on the card-details modal's title bar (LG-360 §8) — one chip
 * per duplicate, variant and series sibling `listRelatedProblems` found for
 * the open problem. Clicking a chip opens the secondary modal
 * (`RelatedProblemModal`) on that related problem; this component only
 * reports which one was picked; the caller owns the open state.
 *
 * Renders nothing for a problem with no relatives, the same "nothing true to
 * say" idiom the rest of this modal uses rather than an empty row.
 */
export function RelatedProblemChips(props: {
  /**
   * The wire shape (`CardDetailsResult['related']`), not the server DTO
   * (LG-360 expert review, B7) — see `RelatedProblemResult`'s doc in
   * `_state/contracts.ts`.
   */
  related: readonly RelatedProblemResult[]
  onOpen: (problemId: string) => void
}): ReactNode {
  const { related, onOpen } = props

  if (related.length === 0) return null

  return (
    <div className="flex max-w-[220px] flex-wrap items-center justify-end gap-1">
      {related.map((item) => (
        <button
          className="max-w-[200px] cursor-pointer truncate rounded-full border border-(--cd-border-2) bg-(--cd-inset) px-2 py-0.5 text-[9.5px] font-semibold text-(--cd-text-muted) hover:border-(--cd-border-4) hover:text-(--cd-text)"
          key={item.problemId}
          onClick={() => onOpen(item.problemId)}
          type="button"
        >
          {chipLabel(item)}
        </button>
      ))}
    </div>
  )
}
